import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import ScrollToTop from "./components/ScrollToTop";

// Import components
import Home from './components/Home';
// import AboutUs from './components/AboutUs';
import Gallery from './components/Gallery';
import RSVP from './components/RSVP';
import FAQ from './components/FAQ';
import MeetTheFamily from './components/MeetTheFamily';
import Itinerary from './components/Itinerary';
import HotelInformation from './components/HotelInformation';
import Login from './components/Login';

// Create a navigation component that changes based on current route
const Navigation = ({ menuOpen, setMenuOpen, userEvent }) => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  
  return (
    <nav className={`navigation ${menuOpen ? 'open' : ''} ${isHomePage ? 'home-nav' : 'page-nav'}`}>
      <ul>
        <li><Link to="/" onClick={() => setMenuOpen(false)}>Home</Link></li>
        <li><Link to="/itinerary" onClick={() => setMenuOpen(false)}>Itinerary</Link></li>
        
        {/* Only show Hotel Information link for Cancun or Both event types */}
        {(userEvent === 'Cancun' || userEvent === 'Both') && (
          <li><Link to="/hotel-information" onClick={() => setMenuOpen(false)}>Hotel Information</Link></li>
        )}
        
        {/* <li><Link to="/about-us" onClick={() => setMenuOpen(false)}>About Us</Link></li> */}
        <li><Link to="/meet-the-family" onClick={() => setMenuOpen(false)}>Meet The Ali's</Link></li>
        <li><Link to="/gallery" onClick={() => setMenuOpen(false)}>Gallery</Link></li>
        <li><Link to="/faq" onClick={() => setMenuOpen(false)}>FAQ</Link></li>
        <li className="rsvp-nav-item"><Link to="/rsvp" className="rsvp-nav-button" onClick={() => setMenuOpen(false)}>RSVP</Link></li>
      </ul>
    </nav>
  );
};

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userName, setUserName] = useState('');
  const [userEvent, setUserEvent] = useState('');
  
  // Check if login caching is disabled
  const isLoginCacheDisabled = process.env.REACT_APP_DISABLE_LOGIN_CACHE === 'true';

  // Check if user is already authenticated (from localStorage) and if the session is still valid
  useEffect(() => {
    // Skip authentication check if login caching is disabled
    if (isLoginCacheDisabled) {
      return;
    }
    
    const storedAuth = localStorage.getItem('isAuthenticated');
    const storedName = localStorage.getItem('userName');
    const storedEvent = localStorage.getItem('userEvent');
    const loginTimestamp = localStorage.getItem('loginTimestamp');
    
    if (storedAuth === 'true' && storedName && loginTimestamp) {
      // Check if the login timestamp is less than 24 hours old
      const currentTime = new Date().getTime();
      const loginTime = parseInt(loginTimestamp, 10);
      const oneDayInMs = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
      
      if (currentTime - loginTime < oneDayInMs) {
        // Session is still valid
        setIsAuthenticated(true);
        setUserName(storedName);
        setUserEvent(storedEvent || '');
      } else {
        // Session has expired, clear localStorage
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('userName');
        localStorage.removeItem('userEvent');
        localStorage.removeItem('loginTimestamp');
      }
    }
  }, [isLoginCacheDisabled]);

  // useEffect(() => {
  //   const handleScroll = () => {
  //     const isScrolled = window.scrollY > 50;
  //     if (isScrolled !== scrolled) {
  //       setScrolled(isScrolled);
  //     }
  //   };

  //   window.addEventListener('scroll', handleScroll);
  //   return () => {
  //     window.removeEventListener('scroll', handleScroll);
  //   };
  // }, [scrolled]);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const handleLogin = (name, event) => {
    setIsAuthenticated(true);
    setUserName(name);
    setUserEvent(event || '');
    
    // Only store in localStorage if caching is not disabled
    if (!isLoginCacheDisabled) {
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('userName', name);
      localStorage.setItem('userEvent', event || '');
      localStorage.setItem('loginTimestamp', new Date().getTime().toString());
    }
  };

  // Enhanced ProtectedRoute component with event-based access control
  const ProtectedRoute = ({ children, requiredEvents = [] }) => {
    if (!isAuthenticated) {
      return <Navigate to="/login" />;
    }
    
    // If requiredEvents array is empty, allow access to all authenticated users
    // Otherwise, check if the user's event is included in the requiredEvents array
    if (requiredEvents.length > 0 && !requiredEvents.includes(userEvent)) {
      return <Navigate to="/unauthorized" />;
    }
    
    return children;
  };

  // Create a new Unauthorized component
  const Unauthorized = () => (
    <div className="page-container">
      <div className="error-message">
        <h1>Access Restricted</h1>
        <p>You don't have access to this page based on your invitation type.</p>
        <p>If you believe this is an error, please contact us.</p>
        <Link to="/" className="back-button">Return to Home</Link>
      </div>
    </div>
  );

  return (
    <Router>
      <ScrollToTop />
      <div className="App">
        {isAuthenticated && (
          <header className={`header ${scrolled ? 'scrolled' : ''}`}>
            <div className="logo">
              <Link to="/"><img src="/images/icon/icon.png" alt="Anmol & Mubeen" className="logo-image" /></Link>
            </div>
            
            {/* Use the new Navigation component */}
            <Navigation menuOpen={menuOpen} setMenuOpen={setMenuOpen} userEvent={userEvent} />
            
            <div className={`menu-toggle ${menuOpen ? 'open' : ''}`} onClick={toggleMenu}>
              <div className="hamburger"></div>
            </div>
          </header>
        )}

        <main>
          <Routes>
            <Route path="/login" element={!isAuthenticated ? <Login onLogin={handleLogin} /> : <Navigate to="/" />} />
            <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
            <Route path="/itinerary" element={<ProtectedRoute><Itinerary userEvent={userEvent} /></ProtectedRoute>} />
            <Route path="/hotel-information" element={
              <ProtectedRoute requiredEvents={['Cancun', 'Both']}>
                <HotelInformation />
              </ProtectedRoute>
            } />
            {/* <Route path="/about-us" element={<ProtectedRoute><AboutUs /></ProtectedRoute>} /> */}
            <Route path="/meet-the-family" element={<ProtectedRoute><MeetTheFamily /></ProtectedRoute>} />
            <Route path="/gallery" element={<ProtectedRoute><Gallery /></ProtectedRoute>} />
            <Route path="/faq" element={<ProtectedRoute><FAQ  userEvent={userEvent} /></ProtectedRoute>} />
            <Route path="/rsvp" element={<ProtectedRoute><RSVP /></ProtectedRoute>} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="*" element={<Navigate to={isAuthenticated ? "/" : "/login"} />} />
          </Routes>
        </main>

        {isAuthenticated && (
          <footer>
            <div className="footer-content">
              <div className="social-links">
                <a href="mailto:info@thealis25.com">Email</a>
              </div>
              <p className="copyright">© 2024 Anmol & Mubeen. All rights reserved.</p>
            </div>
          </footer>
        )}
      </div>
    </Router>
  );
}

export default App;
